/**
 * DTC Overview Page
 * Main dashboard for Direct-to-Consumer sales analytics (Shopify, Amazon)
 */

import React, { useState, useEffect } from 'react'
import { DollarSign, ShoppingCart, TrendingUp, Users, Package, RefreshCw, ArrowRight, Mail, MessageSquare, MapPin, Percent, Repeat, Target, Brain, Zap, Activity, BarChart3, ChevronDown, ChevronUp, CheckCircle, XCircle, Clock, AlertCircle, Database } from 'lucide-react'
import { Link, useLocation } from 'wouter'
import { useTenant } from '../../contexts/TenantContext'
import { useDtcKpis, useDtcPlatforms, useDtcProducts, useDtcCustomers, useDtcSyncStatus, useTriggerDtcSync, useAggregateDtcSales, formatDtcCurrency, formatDtcNumber, formatDtcPercent } from '../../lib/dtc-data-service'
import { useInvalidateCache } from '../../lib/query-cache'
import DateRangeSelector, { ViewType, CalendarRange, getCalendarDays } from '../../components/DateRangeSelector'
import DtcHeader from '../../components/DtcHeader'
import HeroKpiCard from '../../components/HeroKpiCard'
import DtcAIInsightsPanel from '../../components/DtcAIInsightsPanel'
import DtcShopifySalesChart from '../../components/DtcShopifySalesChart'

export default function DtcOverview() {
  const { dtcPlatforms } = useTenant()
  
  // Get current route location
  const [location] = useLocation()
  
  // Local state for platform selection (separate from global context)
  const [localSelectedPlatformId, setLocalSelectedPlatformId] = useState<string | null>(null)
  
  // Date range state (calendar only - no fiscal weeks for DTC)
  const [calendarRange, setCalendarRange] = useState<CalendarRange>('last7')
  const [customStartDate, setCustomStartDate] = useState<string>('')
  const [customEndDate, setCustomEndDate] = useState<string>('')
  const [activeCustomDates, setActiveCustomDates] = useState<{start: string, end: string} | null>(null)
  
  // Collapsible sections (start collapsed for cleaner page load)
  const [showAIInsights, setShowAIInsights] = useState(false) // AI Insights collapsed by default
  const [showRevenueTrend, setShowRevenueTrend] = useState(true) // Daily sales expanded by default
  
  // Aggregation state
  const [aggregationMessage, setAggregationMessage] = useState<string | null>(null)
  const aggregateSales = useAggregateDtcSales()
  
  // Fetch platforms
  const { data: platforms, isLoading: platformsLoading } = useDtcPlatforms()
  
  // Find platform objects by type
  const shopifyPlatform = platforms?.find(p => p.platform_type === 'shopify' || p.id === '30000000-0000-0000-0000-000000000001')
  const amazonPlatform = platforms?.find(p => p.platform_type === 'amazon_seller_central')
  const walmartPlatform = platforms?.find(p => p.platform_type === 'walmart')
  
  const shopifyPlatformId = shopifyPlatform?.id || null
  
  // 🔧 FIX: Auto-detect platform from route to prevent data aggregation bug
  // When navigating to /dtc/shopify, /dtc/amazon, or /dtc/walmart, automatically
  // set the platform filter to show only that platform's data (not all platforms)
  useEffect(() => {
    if (!platforms || platforms.length === 0) return
    
    console.log('[DTC Overview] Route detection - location:', location)
    
    if (location.includes('/dtc/shopify') && shopifyPlatform) {
      console.log('[DTC Overview] Setting platform to Shopify:', shopifyPlatform.id)
      setLocalSelectedPlatformId(shopifyPlatform.id)
    } else if (location.includes('/dtc/amazon') && amazonPlatform) {
      console.log('[DTC Overview] Setting platform to Amazon:', amazonPlatform.id)
      setLocalSelectedPlatformId(amazonPlatform.id)
    } else if (location.includes('/dtc/walmart') && walmartPlatform) {
      console.log('[DTC Overview] Setting platform to Walmart:', walmartPlatform.id)
      setLocalSelectedPlatformId(walmartPlatform.id)
    } else if (location.includes('/dtc/summary') || location === '/dtc/overview') {
      // Summary/overview page should show all platforms (null = aggregate)
      console.log('[DTC Overview] Setting platform to ALL (aggregate)')
      setLocalSelectedPlatformId(null)
    }
  }, [location, platforms, shopifyPlatform, amazonPlatform, walmartPlatform])
  
  // Sync functionality
  const triggerSync = useTriggerDtcSync()
  const { data: syncStatus, refetch: refetchSyncStatus } = useDtcSyncStatus(shopifyPlatformId)
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncMessage, setSyncMessage] = useState<string | null>(null)
  
  // Get current sync status - use polled syncStatus data, falling back to platform data
  const currentSyncStatus = syncStatus?.sync_status || shopifyPlatform?.sync_status
  const currentSyncError = syncStatus?.sync_error_message || shopifyPlatform?.sync_error_message
  const currentLastSync = syncStatus?.last_sync_at || shopifyPlatform?.last_sync_at
  
  // Poll for sync status while syncing
  useEffect(() => {
    if (currentSyncStatus === 'syncing' || currentSyncStatus === 'pending' || isSyncing) {
      const interval = setInterval(() => {
        refetchSyncStatus()
      }, 3000) // Poll every 3 seconds
      
      return () => clearInterval(interval)
    }
  }, [currentSyncStatus, isSyncing, refetchSyncStatus])
  
  // Get centralized cache invalidation (see lib/query-cache.ts)
  const invalidate = useInvalidateCache()
  
  // Update local syncing state based on sync status
  useEffect(() => {
    if (currentSyncStatus === 'syncing' || currentSyncStatus === 'pending') {
      setIsSyncing(true)
      setSyncMessage(currentSyncStatus === 'pending' ? 'Sync pending...' : 'Syncing data from Shopify...')
    } else if (currentSyncStatus === 'success' && isSyncing) {
      setIsSyncing(false)
      setSyncMessage('✅ Sync completed successfully!')
      setTimeout(() => setSyncMessage(null), 5000) // Clear after 5 seconds
      // ✅ Use centralized cache invalidation for consistency
      invalidate.dtc.afterSync(shopifyPlatformId || undefined)
    } else if (currentSyncStatus === 'error' && isSyncing) {
      setIsSyncing(false)
      setSyncMessage('❌ Sync failed. See error below.')
      setTimeout(() => setSyncMessage(null), 10000)
    } else if (currentSyncStatus === 'idle' && isSyncing) {
      // Status was reset to idle (e.g., after failed trigger)
      setIsSyncing(false)
    }
  }, [currentSyncStatus, isSyncing, invalidate, shopifyPlatformId])
  
  const handleSyncNow = async () => {
    if (!shopifyPlatformId) {
      alert('Shopify platform not found')
      return
    }
    
    setIsSyncing(true)
    setSyncMessage('Starting sync...')
    
    try {
      await triggerSync.mutateAsync({
        platform_id: shopifyPlatformId,
        full_sync: false // 7 days by default
      })
      setSyncMessage('Sync initiated! Fetching data from Shopify...')
    } catch (error: any) {
      setIsSyncing(false)
      setSyncMessage(`❌ Failed to start sync: ${error.message || 'Unknown error'}`)
      setTimeout(() => setSyncMessage(null), 8000)
    }
  }
  
  // Calculate date range for API call
  // 🔧 FIX: Use explicit start_date/end_date to match Overview Dashboard calculation
  // This ensures consistent date ranges across all DTC pages (single source of truth)
  let apiOptions: any = {}
  
  if (calendarRange === 'custom' && activeCustomDates) {
    // Send start_date and end_date directly to API
    apiOptions.start_date = activeCustomDates.start
    apiOptions.end_date = activeCustomDates.end
    console.log('[DTC Overview] Custom date range:', { 
      start_date: activeCustomDates.start, 
      end_date: activeCustomDates.end
    })
  } else if (calendarRange !== 'custom') {
    // Calculate explicit dates to match Overview Dashboard logic
    const end = new Date(); // Today
    const days = getCalendarDays(calendarRange);
    const start = new Date(end);
    start.setDate(start.getDate() - days + 1); // +1 to make it inclusive (e.g., 30 days = today - 29)
    
    apiOptions.start_date = start.toISOString().split('T')[0];
    apiOptions.end_date = end.toISOString().split('T')[0];
    
    console.log('[DTC Overview] Calculated date range:', {
      calendarRange,
      days,
      start_date: apiOptions.start_date,
      end_date: apiOptions.end_date
    })
  }
  
  // Fetch KPIs (null platformId = aggregate all platforms)
  const { data: kpiData, isLoading: kpisLoading, refetch } = useDtcKpis(
    localSelectedPlatformId, // null = all platforms aggregated
    undefined, // No single date parameter
    apiOptions
  )
  
  const handleApplyCustomRange = () => {
    if (customStartDate && customEndDate) {
      console.log('[handleApplyCustomRange] Setting dates:', { 
        customStartDate, 
        customEndDate,
        startType: typeof customStartDate,
        endType: typeof customEndDate 
      })
      setActiveCustomDates({ start: customStartDate, end: customEndDate })
      // Don't call refetch() here - React Query will automatically refetch when query dependencies change
    }
  }
  
  const handleRefreshData = async () => {
    setAggregationMessage('Aggregating sales data from all platforms...')
    
    try {
      const result = await aggregateSales.mutateAsync({ days: 90, force: false })
      
      if (result.success) {
        setAggregationMessage(`✅ ${result.message} - Processed ${result.records_processed.amazon} Amazon records`)
        setTimeout(() => {
          setAggregationMessage(null)
          refetch()
        }, 3000)
      }
    } catch (error) {
      setAggregationMessage('❌ Failed to aggregate data: ' + (error instanceof Error ? error.message : 'Unknown error'))
      setTimeout(() => setAggregationMessage(null), 5000)
    }
  }
  
  // Fetch product data for product highlights
  const { data: productsData } = useDtcProducts(null, { days: 1, limit: 5, sort: 'revenue', sort_dir: 'desc' })
  
  // Fetch customer data for customer highlights
  const { data: customersData } = useDtcCustomers(null, { days: 1 })

  const handlePlatformChange = (platformId: string) => {
    setLocalSelectedPlatformId(platformId || null)
  }

  const selectedPlatform = platforms?.find(p => p.id === localSelectedPlatformId)

  if (platformsLoading) {
    return (
      <div>
        <DtcHeader title="DTC Overview" subtitle="Loading..." />
      </div>
    )
  }

  if (!platforms || platforms.length === 0) {
    return (
      <div>
        <DtcHeader title="DTC Overview" subtitle="Direct-to-Consumer Analytics" />
        <div className="p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <Package className="w-12 h-12 mx-auto text-yellow-600 mb-3" />
            <h3 className="text-lg font-semibold text-yellow-900 mb-2">No DTC Platforms Connected</h3>
            <p className="text-yellow-700 mb-4">
              Connect your Shopify or Amazon Seller Central account to view DTC analytics.
            </p>
            <a
              href="/dtc/settings"
              className="inline-flex items-center px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
            >
              Connect Platform
            </a>
          </div>
        </div>
      </div>
    )
  }

  const summary = kpiData?.summary

  // Platform colors
  const platformColors = {
    shopify: { primary: '#95BF47', light: '#F0F7E8', medium: '#C5E09B' }, // Green
    amazon_seller_central: { primary: '#FF9900', light: '#FFF3E0', medium: '#FFD699' }, // Orange
    walmart: { primary: '#0071CE', light: '#E3F2FD', medium: '#90CAF9' }  // Blue
  }
  
  // Determine current platform info based on selected platform ID (already declared above on line 194)
  const currentPlatformType = selectedPlatform?.platform_type || 'shopify'
  const currentPlatformName = selectedPlatform?.platform_name || 'Shopify'
  const currentPlatformColor = platformColors[currentPlatformType as keyof typeof platformColors] || platformColors.shopify

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Platform Color Accent Bar */}
      <div 
        className="h-1 w-full"
        style={{ backgroundColor: currentPlatformColor.primary }}
      />
      
      {/* Breadcrumbs */}
      <div className="bg-white border-b border-gray-200 px-6 py-2">
        <div className="max-w-[1400px] mx-auto">
          <nav className="flex items-center text-sm text-gray-600">
            <Link href="/dtc/summary">
              <a className="hover:text-purple-600 transition-colors">DTC Summary</a>
            </Link>
            <span className="mx-2">›</span>
            <span className="font-semibold" style={{ color: currentPlatformColor.primary }}>
              {currentPlatformName}
            </span>
          </nav>
        </div>
      </div>
      
      {/* DTC-Specific Header */}
      <DtcHeader
        title={`${currentPlatformName} Detail`}
        subtitle={`${currentPlatformName} Store Analytics`}
        onRefresh={refetch}
        isLoading={kpisLoading}
        platforms={platforms}
        selectedPlatformId={localSelectedPlatformId}
        onPlatformChange={handlePlatformChange}
      />

      {/* Main Content Container - Centered with max-width */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-6 space-y-6">
        {/* Data Refresh Section */}
        <div className="bg-white rounded-lg border-2 border-gray-200 shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
                <Database className="w-4 h-4 text-green-600" />
                DTC Data Aggregation
              </h3>
              <p className="text-xs text-gray-600">
                Combines data from all platforms (Shopify, Amazon, Walmart) into a unified view
              </p>
              
              {aggregationMessage && (
                <div className={`mt-2 px-3 py-2 rounded-md text-sm ${
                  aggregationMessage.includes('✅') ? 'bg-green-50 text-green-700 border border-green-200' :
                  aggregationMessage.includes('❌') ? 'bg-red-50 text-red-700 border border-red-200' :
                  'bg-blue-50 text-blue-700 border border-blue-200'
                }`}>
                  {aggregationMessage}
                </div>
              )}
            </div>
            
            <button
              onClick={handleRefreshData}
              disabled={aggregateSales.isPending}
              className="ml-4 px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg shadow-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${aggregateSales.isPending ? 'animate-spin' : ''}`} />
              {aggregateSales.isPending ? 'Refreshing...' : 'Refresh Data'}
            </button>
          </div>
        </div>

        {/* Date Range Selector - Calendar only */}
        <div className="bg-gradient-to-r from-green-50 to-lime-50 rounded-xl border-2 border-green-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-semibold text-gray-800 flex items-center gap-2">
              📅 Date Range
            </span>
            {calendarRange !== 'custom' && apiOptions.start_date && apiOptions.end_date && (
              <span className="text-xs text-gray-600">
                {apiOptions.start_date} to {apiOptions.end_date}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap mb-3">
            <button 
              onClick={() => { setCalendarRange('last7'); setActiveCustomDates(null); }} 
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                calendarRange === 'last7' 
                  ? 'bg-gradient-to-r from-green-600 to-lime-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-green-300 hover:bg-green-50'
              }`}
            >
              Last 7 Days
            </button>
            <button 
              onClick={() => { setCalendarRange('last30'); setActiveCustomDates(null); }} 
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                calendarRange === 'last30' 
                  ? 'bg-gradient-to-r from-green-600 to-lime-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-green-300 hover:bg-green-50'
              }`}
            >
              Last 30 Days
            </button>
            <button 
              onClick={() => { setCalendarRange('last90'); setActiveCustomDates(null); }} 
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                calendarRange === 'last90' 
                  ? 'bg-gradient-to-r from-green-600 to-lime-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-green-300 hover:bg-green-50'
              }`}
            >
              Last 90 Days
            </button>
            <button 
              onClick={() => setCalendarRange('custom')} 
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                calendarRange === 'custom' 
                  ? 'bg-gradient-to-r from-green-600 to-lime-600 text-white shadow-md' 
                  : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-green-300 hover:bg-green-50'
              }`}
            >
              📆 Custom Range
            </button>
          </div>

          {/* Custom Date Range Picker */}
          {calendarRange === 'custom' && (
            <div className="mt-4 p-4 bg-white rounded-lg border-2 border-green-200 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    max={customEndDate || new Date().toISOString().split('T')[0]}
                    min="2018-10-01"
                    className="w-full px-4 py-3 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    min={customStartDate || '2018-10-01'}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 text-sm border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                  />
                </div>
              </div>

              {/* View Report Button */}
              <button
                onClick={handleApplyCustomRange}
                disabled={!customStartDate || !customEndDate}
                className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-lime-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg hover:from-green-700 hover:to-lime-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${kpisLoading ? 'animate-spin' : ''}`} />
                {kpisLoading ? 'Loading...' : 'View Report'}
              </button>

              {/* Selected Range Display */}
              {customStartDate && customEndDate && (
                <p className="text-xs text-gray-600 mt-3 text-center">
                  Will show: <strong>{customStartDate}</strong> to <strong>{customEndDate}</strong>
                  {activeCustomDates && activeCustomDates.start === customStartDate && activeCustomDates.end === customEndDate && (
                    <span className="ml-2 text-green-600">✓ Currently viewing</span>
                  )}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Sync Now Button and Status */}
        <div className="bg-white rounded-lg border-2 border-gray-200 shadow-sm p-5">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
                <RefreshCw className="w-4 h-4" style={{ color: currentPlatformColor.primary }} />
                {currentPlatformName} Data Sync
              </h3>
              <p className="text-xs text-gray-600">
                {currentLastSync 
                  ? `Last synced: ${new Date(currentLastSync).toLocaleString()}`
                  : 'Never synced'
                }
              </p>
              
              {/* Sync Status Message */}
              {syncMessage && (
                <div className={`mt-2 px-3 py-2 rounded-md text-sm ${
                  syncMessage.includes('✅') ? 'bg-green-50 text-green-700 border border-green-200' :
                  syncMessage.includes('❌') ? 'bg-red-50 text-red-700 border border-red-200' :
                  'bg-blue-50 text-blue-700 border border-blue-200'
                }`}>
                  {syncMessage}
                </div>
              )}
              
              {/* Error Message from Platform */}
              {currentSyncStatus === 'error' && currentSyncError && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                  <strong>Error:</strong> {currentSyncError}
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-3 ml-4">
              {/* Status Indicator */}
              <div className="flex items-center gap-2">
                {currentSyncStatus === 'syncing' || isSyncing ? (
                  <div className="flex items-center gap-2 text-blue-600">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span className="text-sm font-medium">Syncing...</span>
                  </div>
                ) : currentSyncStatus === 'pending' ? (
                  <div className="flex items-center gap-2 text-yellow-600">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm font-medium">Pending</span>
                  </div>
                ) : currentSyncStatus === 'success' || currentSyncStatus === 'idle' ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Ready</span>
                  </div>
                ) : currentSyncStatus === 'error' ? (
                  <div className="flex items-center gap-2 text-red-600">
                    <XCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Error</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm font-medium">Unknown</span>
                  </div>
                )}
              </div>
              
              {/* Sync Now Button */}
              <button
                onClick={handleSyncNow}
                disabled={isSyncing || currentSyncStatus === 'syncing' || currentSyncStatus === 'pending'}
                className="px-4 py-2 text-white font-semibold rounded-lg shadow-md hover:shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                style={{ backgroundColor: isSyncing ? '#ccc' : currentPlatformColor.primary }}
              >
                <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
                {isSyncing ? 'Syncing...' : 'Sync Now'}
              </button>
            </div>
          </div>
        </div>

        {/* Hero KPI Cards - 2 Rows of 4 with LW/LY comparisons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <HeroKpiCard
            label="Total Revenue"
            value={kpisLoading ? '...' : formatDtcCurrency(summary?.total_revenue || 0)}
            icon={DollarSign}
            wowDeltaText={summary?.revenue_growth_prev !== null && summary?.revenue_growth_prev !== undefined 
              ? `${summary.revenue_growth_prev >= 0 ? '+' : ''}${(summary.revenue_growth_prev * 100).toFixed(1)}% LW` 
              : undefined}
            wowDeltaTone={summary?.revenue_growth_prev !== undefined && summary?.revenue_growth_prev >= 0 ? 'success' : 'danger'}
            yoyDeltaText={summary?.revenue_growth_yoy !== null && summary?.revenue_growth_yoy !== undefined 
              ? `${summary.revenue_growth_yoy >= 0 ? '+' : ''}${(summary.revenue_growth_yoy * 100).toFixed(1)}% LY` 
              : undefined}
            yoyDeltaTone={summary?.revenue_growth_yoy !== undefined && summary?.revenue_growth_yoy >= 0 ? 'success' : 'danger'}
          />

          <HeroKpiCard
            label="Total Orders"
            value={kpisLoading ? '...' : formatDtcNumber(summary?.total_orders || 0)}
            icon={ShoppingCart}
            wowDeltaText={summary?.orders_growth_prev !== null && summary?.orders_growth_prev !== undefined 
              ? `${summary.orders_growth_prev >= 0 ? '+' : ''}${(summary.orders_growth_prev * 100).toFixed(1)}% LW` 
              : undefined}
            wowDeltaTone={summary?.orders_growth_prev !== undefined && summary?.orders_growth_prev >= 0 ? 'success' : 'danger'}
            yoyDeltaText={summary?.orders_growth_yoy !== null && summary?.orders_growth_yoy !== undefined 
              ? `${summary.orders_growth_yoy >= 0 ? '+' : ''}${(summary.orders_growth_yoy * 100).toFixed(1)}% LY` 
              : undefined}
            yoyDeltaTone={summary?.orders_growth_yoy !== undefined && summary?.orders_growth_yoy >= 0 ? 'success' : 'danger'}
          />

          <HeroKpiCard
            label="Avg Order Value"
            value={kpisLoading ? '...' : formatDtcCurrency(summary?.avg_order_value || 0)}
            icon={Target}
            wowDeltaText={summary?.aov_growth_prev !== null && summary?.aov_growth_prev !== undefined 
              ? `${summary.aov_growth_prev >= 0 ? '+' : ''}${(summary.aov_growth_prev * 100).toFixed(1)}% LW` 
              : undefined}
            wowDeltaTone={summary?.aov_growth_prev !== undefined && summary?.aov_growth_prev >= 0 ? 'success' : 'danger'}
            yoyDeltaText={summary?.aov_growth_yoy !== null && summary?.aov_growth_yoy !== undefined 
              ? `${summary.aov_growth_yoy >= 0 ? '+' : ''}${(summary.aov_growth_yoy * 100).toFixed(1)}% LY` 
              : undefined}
            yoyDeltaTone={summary?.aov_growth_yoy !== undefined && summary?.aov_growth_yoy >= 0 ? 'success' : 'danger'}
          />

          <HeroKpiCard
            label="Total Customers"
            value="546K"
            icon={Users}
            yoyDeltaText="All-time customer base"
            yoyDeltaTone="neutral"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <HeroKpiCard
            label="Total Units Sold"
            value={kpisLoading ? '...' : formatDtcNumber(summary?.total_units || 0)}
            icon={Package}
            wowDeltaText={summary?.units_growth_prev !== null && summary?.units_growth_prev !== undefined 
              ? `${summary.units_growth_prev >= 0 ? '+' : ''}${(summary.units_growth_prev * 100).toFixed(1)}% LW` 
              : undefined}
            wowDeltaTone={summary?.units_growth_prev !== undefined && summary?.units_growth_prev >= 0 ? 'success' : 'danger'}
            yoyDeltaText={summary?.units_growth_yoy !== null && summary?.units_growth_yoy !== undefined 
              ? `${summary.units_growth_yoy >= 0 ? '+' : ''}${(summary.units_growth_yoy * 100).toFixed(1)}% LY` 
              : undefined}
            yoyDeltaTone={summary?.units_growth_yoy !== undefined && summary?.units_growth_yoy >= 0 ? 'success' : 'danger'}
          />

          <HeroKpiCard
            label="Net Revenue"
            value={kpisLoading ? '...' : formatDtcCurrency(summary?.net_revenue || 0)}
            icon={DollarSign}
            yoyDeltaText="After discounts"
            yoyDeltaTone="success"
          />

          <HeroKpiCard
            label="Total Discounts"
            value={kpisLoading ? '...' : formatDtcCurrency(summary?.discount_total || 0)}
            icon={Percent}
            yoyDeltaText={`${((summary?.discount_total || 0) / (summary?.total_revenue || 1) * 100).toFixed(1)}% of gross`}
            yoyDeltaTone="neutral"
          />

          <HeroKpiCard
            label="Avg Daily Revenue"
            value={kpisLoading ? '...' : formatDtcCurrency((summary?.total_revenue || 0) / Math.max(calendarRange !== 'custom' ? getCalendarDays(calendarRange) : 90, 1))}
            icon={TrendingUp}
            yoyDeltaText="Per day average"
            yoyDeltaTone="neutral"
          />
        </div>

        {/* DTC AI Insights Panel - Collapsible */}
        {apiOptions.start_date && apiOptions.end_date && (
          <div className="bg-white rounded-lg border-2 border-gray-200 shadow-sm overflow-hidden">
            <button
              onClick={() => setShowAIInsights(!showAIInsights)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-600" />
                PatternIQ Insights
              </h3>
              {showAIInsights ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
            
            {showAIInsights && (
              <div className="border-t border-gray-200">
                <DtcAIInsightsPanel
                  platformId={localSelectedPlatformId}
                  startDate={apiOptions.start_date}
                  endDate={apiOptions.end_date}
                  maxInsights={8}
                />
              </div>
            )}
          </div>
        )}

        {/* Shopify Sales Bar Chart - Driven by date range picker */}
        {kpiData?.trend && kpiData.trend.length > 0 && (
          <DtcShopifySalesChart
            data={kpiData.trend}
            platformColor={currentPlatformColor.primary}
            platformName={currentPlatformName}
            className="mb-6"
          />
        )}

          {/* Daily Sales Trend - Collapsible (matches Walmart format) */}
          {kpiData?.trend && kpiData.trend.length > 0 && (
            <div className="bg-white rounded-lg border-2 border-gray-200 shadow-sm overflow-hidden">
              <button
                onClick={() => setShowRevenueTrend(!showRevenueTrend)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" style={{ color: currentPlatformColor.primary }} />
                  Daily Sales Trend ({kpiData.trend.length} days)
                </h3>
                {showRevenueTrend ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </button>
              
              {showRevenueTrend && (
                <div className="px-6 pb-6 border-t border-gray-200">
                  <div className="max-h-96 overflow-y-auto">
                    <table className="w-full">
                      <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Date</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Revenue</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Orders</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Units</th>
                          <th className="text-right py-3 px-4 text-xs font-semibold text-gray-600 uppercase">AOV</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {Array.isArray(kpiData?.trend) && kpiData.trend.slice().reverse().map((point, idx) => {
                          // Handle both date formats: "2025-11-07" and "2025-11-07T00:00:00.000Z"
                          const dateStr = point.date.includes('T') ? point.date : point.date + 'T00:00:00Z'
                          return (
                          <tr key={idx} className="hover:bg-green-50 transition-colors">
                            <td className="py-3 px-4 text-sm text-gray-900 font-medium">
                              {new Date(dateStr).toLocaleDateString('en-US', { 
                                weekday: 'short', 
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                timeZone: 'UTC'
                              })}
                            </td>
                            <td className="py-3 px-4 text-sm text-right font-semibold text-green-600">
                              {formatDtcCurrency(point.total_revenue)}
                            </td>
                            <td className="py-3 px-4 text-sm text-right text-gray-700">
                              {formatDtcNumber(point.total_orders)}
                            </td>
                            <td className="py-3 px-4 text-sm text-right text-gray-700">
                              {formatDtcNumber(point.total_units)}
                            </td>
                            <td className="py-3 px-4 text-sm text-right font-medium" style={{ color: currentPlatformColor.primary }}>
                              {formatDtcCurrency(point.avg_order_value)}
                            </td>
                          </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-3 text-xs text-gray-500 text-center">
                    Showing {kpiData.trend.length} days • Click to collapse
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ML/AI Coming Soon Section */}
          <div className="bg-gradient-to-br from-green-600 via-green-700 to-lime-800 rounded-2xl shadow-xl p-8 text-white">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                <Brain className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">🚀 AI-Powered DTC Intelligence</h2>
                <p className="text-green-100 text-sm mt-1">Unlock predictive analytics for your Shopify business</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Revenue Forecasting */}
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20 hover:bg-white/15 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <TrendingUp className="w-6 h-6 text-cyan-300" />
                  <h3 className="font-semibold text-lg">Revenue Forecasting</h3>
                </div>
                <p className="text-sm text-green-100 mb-3">
                  Predict next 30/60/90 days revenue with 85%+ accuracy using your full year of data
                </p>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-white/20 text-white text-xs font-medium rounded-full border border-white/30">
                    Ready to Build
                  </span>
                  <span className="text-xs text-green-200">HIGH PRIORITY</span>
                </div>
              </div>

              {/* Customer Churn Prediction */}
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20 hover:bg-white/15 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Users className="w-6 h-6 text-pink-300" />
                  <h3 className="font-semibold text-lg">Churn Prediction</h3>
                </div>
                <p className="text-sm text-green-100 mb-3">
                  Identify at-risk customers and trigger win-back campaigns automatically
                </p>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-yellow-500/20 text-yellow-300 text-xs font-medium rounded-full border border-yellow-400/30">
                    Planned Q1 2026
                  </span>
                </div>
              </div>

              {/* Product Demand Forecasting */}
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20 hover:bg-white/15 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Package className="w-6 h-6 text-emerald-300" />
                  <h3 className="font-semibold text-lg">Demand Forecasting</h3>
                </div>
                <p className="text-sm text-green-100 mb-3">
                  SKU-level demand prediction for inventory optimization
                </p>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-white/20 text-white text-xs font-medium rounded-full border border-white/30">
                    Ready to Build
                  </span>
                </div>
              </div>

              {/* Customer Segmentation AI */}
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20 hover:bg-white/15 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Target className="w-6 h-6 text-orange-300" />
                  <h3 className="font-semibold text-lg">AI Segmentation</h3>
                </div>
                <p className="text-sm text-green-100 mb-3">
                  Auto-cluster 546K customers for personalized campaigns
                </p>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-white/20 text-white text-xs font-medium rounded-full border border-white/30">
                    Ready to Build
                  </span>
                </div>
              </div>

              {/* Dynamic Pricing */}
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20 hover:bg-white/15 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <DollarSign className="w-6 h-6 text-yellow-300" />
                  <h3 className="font-semibold text-lg">Pricing Optimizer</h3>
                </div>
                <p className="text-sm text-green-100 mb-3">
                  AI-recommended pricing to maximize margin and revenue
                </p>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-yellow-500/20 text-yellow-300 text-xs font-medium rounded-full border border-yellow-400/30">
                    Planned Q1 2026
                  </span>
                </div>
              </div>

              {/* Email Campaign Optimizer */}
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-5 border border-white/20 hover:bg-white/15 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Mail className="w-6 h-6 text-blue-300" />
                  <h3 className="font-semibold text-lg">Campaign Optimizer</h3>
                </div>
                <p className="text-sm text-green-100 mb-3">
                  Best send times, product recommendations for 269K subscribers
                </p>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-yellow-500/20 text-yellow-300 text-xs font-medium rounded-full border border-yellow-400/30">
                    Planned Q2 2026
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-white/10 rounded-lg border border-white/20">
              <p className="text-sm text-green-100">
                💡 <strong>Your data is ready!</strong> With 366 days of order history, 546K customers, and 239 products, you have everything needed to unlock these AI features.
              </p>
            </div>
          </div>
      </div>
    </div>
  )
}

